"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Navbar } from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import CookieConsent from "@/components/shared/CookieConsent";
import ScrollToTopButton from "@/components/shared/ScrollToTopButton";
import GoogleTagManager from "@/components/shared/GoogleTagManager";
import { ContactSpeedDial } from "@/components/shared/ContactSpeedDial";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { trackPageView, initScrollTracking, debugGAStatus } from "@/lib/analytics";
import { initAutoTracking } from "@/lib/analytics/auto-tracker";
import { getLocaleFromPathname, localeConfigs } from "@/lib/i18n";
import { LocaleProvider } from "@/content/i18n/context";
import ContentIdeaPopup from "@/components/common/ContentIdeaPopup";
import DeferredStyles from "@/components/shared/DeferredStyles";

export default function ClientLayout({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  // Get Google Tag Manager ID from environment variable
  const gaId = process.env.NEXT_PUBLIC_GTM_ID || "";

  // Get current pathname for unique page keys
  const pathname = usePathname();
  const currentLocale = getLocaleFromPathname(pathname);

  // The page fade runs on client-side navigation only. On the first load the
  // content must be visible in the static HTML: with initial opacity 0 nothing
  // painted until JS hydrated, which put LCP at 2.8s on ~200 URLs (GSC Core
  // Web Vitals, 2026-10-10). initial={false} renders the final state.
  const isFirstRender = useRef(true);
  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  useEffect(() => {
    document.documentElement.lang = localeConfigs[currentLocale].hreflang;
  }, [currentLocale]);

  // Track page views and initialize analytics
  useEffect(() => {
    console.log(`🔄 ClientLayout: Route changed to ${pathname}`);

    // Small delay to ensure GA is initialized
    const timer = setTimeout(() => {
      trackPageView(pathname);

      // Debug GA status in development
      if (process.env.NODE_ENV === "development") {
        debugGAStatus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname]);

  // Initialize scroll tracking and auto-tracking on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      initScrollTracking();
      initAutoTracking(process.env.NODE_ENV === "development");
    }, 1000); // Wait 1 second for GA to be ready

    return () => clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <LocaleProvider locale={currentLocale}>
          <GoogleTagManager gaId={gaId} />
          <DeferredStyles locale={currentLocale} />
          <Toaster />
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <motion.div
                key={pathname}
                initial={isFirstRender.current ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                {children}
              </motion.div>
            </main>
            <Footer />
            <CookieConsent />
            {/* After the cookie banner so the contact bubble stays visible over it. */}
            <ContactSpeedDial />
            <ScrollToTopButton />
            {/* Reading pages only — the component owns its own route allowlist
                and fires at 70% scroll + 45s dwell, never on load. */}
            <ContentIdeaPopup />
          </div>
        </LocaleProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
